<script setup lang="ts">
import { computed, reactive, useId } from 'vue'
import { Lock, LockOpen } from '@lucide/vue'

import { RepositoryTimeline, TimelineDraft } from '@types'

import {
  DSButton,
  DSDateField,
  DSFieldLabel,
  DSSelect,
  DSSelectContent,
  DSSelectItem,
  DSSelectTrigger,
  DSSelectValue,
} from '@ds'
import { PERIODS_LIMIT, PERIOD_TYPE_OPTIONS } from './utils/const'

type Props = {
  timeline: RepositoryTimeline
  disabled?: boolean
  simpleView?: boolean
}

type Emits = {
  apply: [draft: TimelineDraft]
  cancel: []
}

const { timeline, disabled = false } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'DSPeriodScrollerSettings',
})

/*
 * A local draft, applied only on "Apply" and discarded on "Cancel"/dismiss —
 * replaces the legacy UITimeline's debounce/blur-coordination submit logic
 * with a plain object. Re-seeded from props every time this component mounts,
 * which is every time the popover/sheet opens (Reka unmounts closed content).
 */
const draft = reactive<TimelineDraft>({
  periods: timeline.periods,
  freq: timeline.freq,
  end: timeline.end,
  end_is_today: timeline.end_is_today,
})

const periodsValue = computed({
  get: () => String(draft.periods),
  set: value => { draft.periods = Number(value) },
})

const periodOptions = Array.from({ length: PERIODS_LIMIT }, (_, index) => String(index + 1))

const periodsId = useId()
const typeId = useId()
const endId = useId()

const toggleLock = () => {
  draft.end_is_today = !draft.end_is_today
}

const draftSummary = computed(() => `${draft.periods} × ${draft.freq}`)

const handleApply = () => {
  emit('apply', { ...draft })
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <span class="text-base font-bold tracking-ds-tight text-foreground">Timeline settings</span>

    <div class="grid grid-cols-[5.75rem_minmax(0,1fr)] gap-3.5">
      <div class="flex flex-col gap-1.5 min-w-0">
        <DSFieldLabel
          :for="periodsId"
          label="Periods"
        />
        <DSSelect v-model="periodsValue">
          <DSSelectTrigger
            :id="periodsId"
            :disabled
          >
            <DSSelectValue />
          </DSSelectTrigger>
          <DSSelectContent>
            <DSSelectItem
              v-for="option in periodOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </DSSelectItem>
          </DSSelectContent>
        </DSSelect>
      </div>

      <div class="flex flex-col gap-1.5 min-w-0">
        <DSFieldLabel
          :for="typeId"
          label="Period type"
        />
        <DSSelect v-model="draft.freq">
          <DSSelectTrigger
            :id="typeId"
            :disabled
          >
            <DSSelectValue />
          </DSSelectTrigger>
          <DSSelectContent>
            <DSSelectItem
              v-for="option in PERIOD_TYPE_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </DSSelectItem>
          </DSSelectContent>
        </DSSelect>
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <DSFieldLabel
        :for="endId"
        label="Periods ending"
      />
      <div class="flex items-center gap-2">
        <DSButton
          v-if="!simpleView"
          type="button"
          variant="ghost"
          size="icon-sm"
          :disabled
          :aria-pressed="!draft.end_is_today"
          :aria-label="draft.end_is_today ? 'Pin the end date' : 'Unpin the end date'"
          :class="!draft.end_is_today ? 'text-destructive hover:text-destructive' : 'text-muted-foreground'"
          @click="toggleLock"
        >
          <component
            :is="draft.end_is_today ? LockOpen : Lock"
            class="size-4"
          />
        </DSButton>
        <DSDateField
          :id="endId"
          v-model="draft.end"
          :disabled
          class="flex-1"
        />
      </div>
      <span
        v-if="!simpleView"
        class="text-[11px] font-medium text-muted-foreground"
      >
        {{ draft.end_is_today
          ? 'End date rolls forward to the latest available period.'
          : 'End date is pinned. Unpin to roll forward automatically.' }}
      </span>
    </div>

    <div
      class="flex items-center justify-end gap-2"
      :class="{ 'border-t border-border pt-3': !simpleView }"
    >
      <span class="flex-1 text-[11px] font-medium text-muted-foreground">
        {{ draftSummary }}
      </span>
      <DSButton
        v-if="!simpleView"
        type="button"
        variant="secondary"
        size="sm"
        @click="emit('cancel')"
      >
        Cancel
      </DSButton>
      <DSButton
        type="button"
        :size="simpleView ? undefined : 'sm'"
        @click="handleApply"
      >
        Apply
      </DSButton>
    </div>
  </div>
</template>
